import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { forEach } from 'lodash-es';
import serialize from '../utils/serialize';
import {
    PublicationId,
    IRollingReturnParams,
    SelectModel,
    StrategyValue,
    CHART_COLORS,
    naToNull,
    PreliminaryIrrResponse,
    HorizonReturnsResponse,
    SubtitlesResponse,
    Article,
    HttpResult
} from '../types';

@Injectable({
    providedIn: 'root'
})
export class ApiService {
    private rollingReturnsCache: IRollingReturnParams | null = null;
    private horizonReturnsCache: HorizonReturnsResponse | null = null;
    private preliminaryIrrCache: PreliminaryIrrResponse | null = null;
    private subtitlesCache: SubtitlesResponse | null = null;
    private articlesCache: {
        [key: string]: Article[];
    } = {};

    constructor(private http: HttpClient) {}

    getArticles(publicationId: PublicationId): Observable<Article[]> {
        if (this.articlesCache[publicationId]) {
            return of(this.articlesCache[publicationId]);
        }

        return this.http
            .get<Article[]>(`/public/peicontent/articles?publicationId=${publicationId}`)
            .pipe(tap(response => (this.articlesCache[publicationId] = response)));
    }

    getRollingReturns(): Observable<IRollingReturnParams> {
        if (this.rollingReturnsCache) {
            return of(this.rollingReturnsCache);
        }

        return this.http.get<IRollingReturnParams>(`/public/pci/rolling-returns`).pipe(
            map(response => this.processRollingReturnsResponse(response)),
            tap(processedData => (this.rollingReturnsCache = processedData))
        );
    }

    getHorizonReturns(): Observable<HorizonReturnsResponse> {
        if (this.horizonReturnsCache) {
            return of(this.horizonReturnsCache);
        }

        return this.http
            .get<HorizonReturnsResponse>(`/public/pci/horizon-returns`)
            .pipe(tap(response => (this.horizonReturnsCache = response)));
    }

    getPreliminaryIrr(): Observable<PreliminaryIrrResponse> {
        if (this.preliminaryIrrCache) {
            return of(this.preliminaryIrrCache);
        }

        return this.http
            .get<PreliminaryIrrResponse>(`/peindex/public/preliminaryIrr`)
            .pipe(tap(response => (this.preliminaryIrrCache = response)));
    }

    getSubtitles(): Observable<SubtitlesResponse> {
        if (this.subtitlesCache) {
            return of(this.subtitlesCache);
        }

        return this.http
            .get<SubtitlesResponse>(`/peindex/public/subtitles`)
            .pipe(tap(response => (this.subtitlesCache = response)));
    }

    getAdtrax(): Observable<{ result: string; success: boolean }> {
        return this.http.get<{ result: string; success: boolean }>(
            '/service/public/v1/config/peiadtrax'
        );
    }

    trackActivity(path: String) {
        const adjustedPath = path.split('?')[0] || 'home';
        let body = serialize({ category: 'public', path: adjustedPath });
        let options = { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } };
        this.http.post('/peindex/public/tracking', body, options).subscribe();
    }

    trackLogin(uri: string): Observable<HttpResult<any>> {
        return this.http
            .get('/service/wtrack/sync', {
                params: {
                    appcode: 'wam',
                    category: 'login',
                    uri
                }
            })
            .pipe(
                switchMap(data => of({ data })),
                catchError(error => of({ error }))
            );
    }

    trackPWDReset(uri: string): Observable<HttpResult<any>> {
        return this.http
            .get('/service/wtrack/sync', {
                params: {
                    appcode: 'wam',
                    category: 'changepwd',
                    uri
                }
            })
            .pipe(
                switchMap(data => of({ data })),
                catchError(error => of({ error }))
            );
    }

    getLoginMsg() {
        return this.http.get('/research/service/public/v1/config/loginMessage', {
            params: {
                cacheBreaker: new Date().getTime()
            }
        });
    }

    private processRollingReturnsResponse(response: any): IRollingReturnParams {
        const processedData = this.processRollingReturnsData(response);
        const metadata = response.metadata;

        if (!processedData || Object.keys(processedData).length === 0) {
            return {
                investmentHorizons: [],
                selectedInvestmentHorizonId: '',
                chartData: null,
                metadata
            };
        }

        // Build investment horizons
        const investmentHorizons: SelectModel[] = [];
        forEach(processedData, (value, key: string) => {
            investmentHorizons.push({
                id: key,
                name: key
            });
        });

        const selectedInvestmentHorizonId =
            investmentHorizons.length > 0 ? investmentHorizons[0].id : '';
        const data = processedData[selectedInvestmentHorizonId];

        if (!data) {
            return {
                investmentHorizons,
                selectedInvestmentHorizonId,
                chartData: null,
                metadata
            };
        }

        // Build chart data
        const orderedStrategies: string[] = [
            StrategyValue.debt,
            StrategyValue.vc,
            StrategyValue.bo
        ];
        const allStrategies = orderedStrategies.concat(['All']);
        const chartData: any[] = [];

        forEach(allStrategies, (strategy: string) => {
            const dataByStrategy = data[strategy];
            if (dataByStrategy) {
                const chartDateItem: {
                    name: string;
                    data: Array<[number, number | null]>;
                    color: string;
                } = {
                    name: strategy,
                    data: [],
                    color: this.getStrategyColor(strategy)
                };
                forEach(dataByStrategy, (value: number, key: string) => {
                    const chartX = this.getTimeFromQuarter(key);
                    const chartY = naToNull(value);
                    chartDateItem.data.push([chartX, chartY]);
                });
                chartData.push(chartDateItem);
            }
        });

        return {
            investmentHorizons,
            selectedInvestmentHorizonId,
            chartData,
            metadata
        };
    }

    private processRollingReturnsData(response: any): any {
        if (
            !response?.data ||
            !Array.isArray(response.data) ||
            !response?.series ||
            !Array.isArray(response.series)
        ) {
            return {};
        }

        // Dynamically build processedData structure from series array
        const processedData: any = {
            Quarterly: {}
        };

        // Initialize strategy objects from series array
        response.series.forEach((series: any) => {
            if (series.name) {
                processedData['Quarterly'][series.name] = {};
            }
        });

        // Process the flat array data
        const data = response.data;
        const seriesCount = response.series.length;

        for (let i = 0; i < data.length; i += seriesCount + 1) {
            const dateStr = data[i]; // e.g., "10/01/1996"

            // Convert date to quarter format (YYYYQQ)
            const quarterKey = this.convertDateToQuarterKey(dateStr);

            if (quarterKey) {
                // Loop through each series and assign values
                response.series.forEach((series: any, index: number) => {
                    const value = data[i + 1 + index]; // +1 to skip date, +index for series position
                    processedData['Quarterly'][series.name][quarterKey] = value;
                });
            }
        }

        return processedData;
    }

    private convertDateToQuarterKey(dateStr: string): string {
        try {
            const date = new Date(dateStr);
            const year = date.getFullYear();
            const month = date.getMonth() + 1; // getMonth() returns 0-11

            let quarter = '';
            if (month >= 1 && month <= 3) {
                quarter = '01';
            } else if (month >= 4 && month <= 6) {
                quarter = '02';
            } else if (month >= 7 && month <= 9) {
                quarter = '03';
            } else if (month >= 10 && month <= 12) {
                quarter = '04';
            }

            return `${year}${quarter}`;
        } catch (error) {
            return '';
        }
    }

    private getTimeFromQuarter(quarter: string): number {
        const year: number = +quarter.substring(0, 4);
        const quarterIndex: number = +quarter.substring(5); // 1,2,3,4
        const quarterEndMonth = quarterIndex * 3; // month is zero based, 0 means January, 11 means December
        const day = 0; // last day of previous month

        const date = new Date(year, quarterEndMonth, day);

        return date.getTime();
    }

    private getStrategyColor(strategy: string): string {
        if (strategy === StrategyValue.bo) {
            return CHART_COLORS.buyoutColor;
        }
        if (strategy === StrategyValue.vc) {
            return CHART_COLORS.ventureCapitalColor;
        }
        if (strategy === StrategyValue.debt) {
            return CHART_COLORS.privateDebtColor;
        }
        return CHART_COLORS.blackColor;
    }

    // Method to clear all cached data
    clearCache(): void {
        this.rollingReturnsCache = null;
        this.horizonReturnsCache = null;
        this.preliminaryIrrCache = null;
        this.subtitlesCache = null;
    }

    // Methods to clear individual caches
    clearRollingReturnsCache(): void {
        this.rollingReturnsCache = null;
    }

    clearHorizonReturnsCache(): void {
        this.horizonReturnsCache = null;
    }

    clearPreliminaryIrrCache(): void {
        this.preliminaryIrrCache = null;
    }

    clearSubtitlesCache(): void {
        this.subtitlesCache = null;
    }
}
